/**
 * Document Viewer — единый клиентский вьюер для ВСЕХ типов файлов чата
 * (pdf, docx, xls/xlsx/csv, txt, md, json, код и пр.).
 *
 * Одна UI-оболочка для всех форматов:
 *   [общий тулбар: зум · навигация листов/страниц · поиск · копировать · ссылка · скачать]
 *   [зона просмотра со скроллом и отступами → «листы»-карточки со скруглением и тенью]
 *
 * Подключение (императивно, без Alpine — надёжнее при смене файла в Livewire):
 *   Blade: <div class="uk-doc-viewer-wrap" wire:key="…" wire:ignore></div> (пустой контейнер)
 *   JS:    window.documentViewerOpen({ type, ext, name, url }) — из обработчика события
 *          Livewire.on('offcanvas-file', …). Оболочка строится один раз на элемент (boot),
 *          каждое открытие переерисовывает содержимое (open).
 *
 * Рендер клиентский; Livewire отдаёт только same-origin url на прокси-роут computer.preview.
 * Библиотеки грузятся лениво. Переопределить источники (например, на public/vendor):
 *   window.DOCUMENT_VIEWER_LIBS = { pdf:'…', pdfWorker:'…', jszip:'…', docxPreview:'…', xlsx:'…', marked:'…' };
 */
(function () {
    'use strict';

    /*
    const LIBS = Object.assign({
        pdf:        'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.min.js',
        pdfWorker:  'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js',
        jszip:      'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js',
        docxPreview:'https://cdn.jsdelivr.net/npm/docx-preview@0.3.2/dist/docx-preview.min.js',
        xlsx:       'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js',
        marked:     'https://cdn.jsdelivr.net/npm/marked@12.0.2/marked.min.js',
    }, window.DOCUMENT_VIEWER_LIBS || {});
    */

    const LIBS = Object.assign({
        pdf:        '/js/components/viewer/pdf.min.js',
        pdfWorker:  '/js/components/viewer/pdf.worker.min.js',
        jszip:      '/js/components/viewer/jszip.min.js',
        docxPreview:'/js/components/viewer/docx-preview.min.js',
        xlsx:       '/js/components/viewer/xlsx.full.min.js',
        exceljs:    '/js/components/viewer/exceljs.min.js', // xlsx со стилями (цвета/границы/merge); MIT
        marked:     '/js/components/viewer/marked.min.js',
    }, window.DOCUMENT_VIEWER_LIBS || {});

    // pdf.js-объекты нельзя класть в реактивное состояние Alpine (Proxy ломает приватные поля #field)
    const PDFS = new WeakMap();

    const _scriptCache = {};
    function loadScript(url) {
        if (_scriptCache[url]) return _scriptCache[url];
        _scriptCache[url] = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = url; s.async = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error('Не удалось загрузить ' + url));
            document.head.appendChild(s);
        });
        return _scriptCache[url];
    }
    async function ensurePdf() {
        if (!window.pdfjsLib) await loadScript(LIBS.pdf);
        if (window.pdfjsLib && !window.pdfjsLib.__workerSet) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = LIBS.pdfWorker;
            window.pdfjsLib.__workerSet = true;
        }
        return window.pdfjsLib;
    }
    async function ensureDocx() {
        if (!window.JSZip) await loadScript(LIBS.jszip);
        if (!window.docx) await loadScript(LIBS.docxPreview);
        return window.docx;
    }
    async function ensureXlsx() {
        if (!window.XLSX) await loadScript(LIBS.xlsx);
        return window.XLSX;
    }
    async function ensureExcel() {
        if (!window.ExcelJS) await loadScript(LIBS.exceljs);
        return window.ExcelJS;
    }
    async function ensureMarked() {
        try { if (!window.marked) await loadScript(LIBS.marked); } catch (e) { /* fallback на <pre> */ }
        return window.marked || null;
    }

    function el(tag, cls, html) {
        const n = document.createElement(tag);
        if (cls) n.className = cls;
        if (html != null) n.innerHTML = html;
        return n;
    }
    function esc(s) {
        return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    }

    // --- Хелперы для ExcelJS-рендера xlsx со стилями ---
    function colToNum(a) { let n = 0; for (let i = 0; i < a.length; i++) n = n * 26 + (a.charCodeAt(i) - 64); return n; }
    function argbToCss(c) {
        if (!c || !c.argb) return null;               // тематические/индексные цвета пропускаем
        const v = c.argb;
        if (v.length === 8) { if (v.slice(0, 2).toUpperCase() === '00') return null; return '#' + v.slice(2); }
        if (v.length === 6) return '#' + v;
        return null;
    }
    function borderCss(bd) {
        const map = { thin: '1px solid', medium: '2px solid', thick: '3px solid',
            dashed: '1px dashed', dotted: '1px dotted', double: '3px double', hair: '1px solid' };
        return (map[bd.style] || '1px solid') + ' ' + (argbToCss(bd.color) || 'rgba(0,0,0,.4)');
    }
    function cellText(cell) { try { const t = cell.text; return t == null ? '' : String(t); } catch (e) { return ''; } }
    async function fetchBuffer(url) {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return await res.arrayBuffer();
    }
    function notify(message, status) {
        if (window.UIkit && UIkit.notification) {
            UIkit.notification({ message, status: (status || 'primary') + ' uk-notification-panel', pos: 'bottom-right', timeout: 2000 });
        }
    }

    window.documentViewer = function (config) {
        return {
            cfg: config || {},
            scale: 1,
            pageCount: 1,
            currentPage: 1,
            fallbackHtml: '',
            root: null, mountEl: null, stageEl: null,
            els: {},
            _pages: [],
            _buffer: null,
            _sheets: null,
            _hits: [], _hitIdx: -1, _pdfMatches: [], _pdfText: {},
            io: null, ro: null,

            // Категории по формату (одна оболочка, разный рендер)
            isPdf()   { return this.cfg.type === 'pdf'; },
            isSheet() { return this.cfg.type === 'sheet'; },
            isWord()  { return this.cfg.type === 'word'; },
            isText()  { return this.cfg.type === 'text'; },
            // где искать/копировать текст в DOM (для не-pdf)
            scope()   { return this.stageEl; },

            // Однократная сборка оболочки (тулбар + строка поиска + сцена). Один раз на элемент.
            boot() {
                this.root.classList.add('uk-doc-viewer');
                this.buildShell();
            },

            // (Пере)открытие файла: сброс состояния предыдущего файла и рендер нового.
            // Вызывается на каждое открытие из window.documentViewerOpen (событие offcanvas-file).
            open(cfg) {
                this.cfg = cfg || {};
                // docx/doc открываем на 60% (у Word-страниц фикс. ширина листа — на 100% велики),
                // остальные типы — 100%.
                this.scale = (this.cfg.type === 'word') ? 0.6 : 1;
                this.pageCount = 1;
                this.currentPage = 1;
                this._pages = [];
                this._buffer = null;
                this._sheets = null;
                this._excel = null; this._excelSheets = null;
                this._hits = []; this._hitIdx = -1;
                this._pdfMatches = []; this._pdfText = {};
                this._refitting = false;
                if (this.io) { this.io.disconnect(); this.io = null; }
                if (this.ro) { this.ro.disconnect(); this.ro = null; }
                PDFS.delete(this);
                this.toggleSearch(false);
                if (this.els.zoom) this.els.zoom.textContent = Math.round(this.scale * 100) + '%';
                // Начальный зум применяем ко всем типам единообразно через CSS zoom сцены.
                if (this.stageEl) this.stageEl.style.zoom = this.scale;
                this.render();
            },

            // ---- Оболочка: тулбар + строка поиска + сцена -----------------------
            buildShell() {
                const bar = el('div', 'uk-doc-viewer__toolbar');

                // Зум
                const zoom = el('div', 'uk-doc-viewer__group');
                zoom.appendChild(this.btn('minus', () => this.zoom(-0.1), 'Уменьшить'));
                this.els.zoom = el('span', 'uk-doc-viewer__label', '100%');
                zoom.appendChild(this.els.zoom);
                zoom.appendChild(this.btn('plus', () => this.zoom(0.1), 'Увеличить'));
                bar.appendChild(zoom);

                // Навигация по листам/страницам
                const nav = el('div', 'uk-doc-viewer__group');
                this.els.prev = this.btn('chevron-left', () => this.go(-1), 'Назад');
                this.els.counter = el('span', 'uk-doc-viewer__counter', '1 / 1');
                this.els.next = this.btn('chevron-right', () => this.go(1), 'Вперёд');
                nav.appendChild(this.els.prev);
                nav.appendChild(this.els.counter);
                nav.appendChild(this.els.next);
                bar.appendChild(nav);

                const spacer = el('div', 'uk-doc-viewer__spacer');
                bar.appendChild(spacer);

                // Действия
                const act = el('div', 'uk-doc-viewer__group');
                act.appendChild(this.btn('search', () => this.toggleSearch(), 'Поиск'));
                act.appendChild(this.btn('copy', () => this.copyAll(), 'Копировать текст'));
                act.appendChild(this.btn('link', () => this.copyLink(), 'Скопировать ссылку'));
                act.appendChild(this.btn('download', () => this.download(), 'Скачать'));
                bar.appendChild(act);

                // Строка поиска (скрыта по умолчанию)
                const search = el('div', 'uk-doc-viewer__search');
                this.els.search = el('input', 'uk-doc-viewer__search-input');
                this.els.search.type = 'text';
                this.els.search.placeholder = 'Поиск по документу…';
                this.els.search.addEventListener('input', () => this.runSearch(this.els.search.value));
                this.els.search.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') { e.preventDefault(); e.shiftKey ? this.hit(-1) : this.hit(1); }
                    if (e.key === 'Escape') this.toggleSearch(false);
                });
                this.els.sCount = el('span', 'uk-doc-viewer__search-count', '');
                search.appendChild(this.els.search);
                search.appendChild(this.els.sCount);
                search.appendChild(this.btn('chevron-up', () => this.hit(-1), 'Предыдущее'));
                search.appendChild(this.btn('chevron-down', () => this.hit(1), 'Следующее'));
                search.appendChild(this.btn('close', () => this.toggleSearch(false), 'Закрыть'));
                this.els.searchRow = search;

                // Сцена
                this.mountEl = el('div', 'uk-doc-viewer__mount');
                this.stageEl = el('div', 'uk-doc-viewer__stage');
                this.mountEl.appendChild(this.stageEl);

                this.root.appendChild(bar);
                this.root.appendChild(search);
                this.root.appendChild(this.mountEl);
            },

            btn(icon, fn, title) {
                const b = el('button', 'uk-doc-viewer__btn', '<span uk-icon="icon: ' + icon + '"></span>');
                b.type = 'button';
                if (title) b.title = title;
                b.addEventListener('click', fn);
                return b;
            },

            setStatus(kind, text) {
                this.stageEl.innerHTML = '';
                if (kind === 'loading') {
                    const box = el('div', 'uk-doc-viewer__status uk-text-center');
                    box.appendChild(el('div', '', '<span class="uk-spinner" uk-spinner="ratio: 1.2"></span>'));
                    box.appendChild(el('p', 'uk-text-muted', text || 'Загрузка…'));
                    this.stageEl.appendChild(box);
                    return;
                }
                if (this.fallbackHtml && this.fallbackHtml.trim() !== '') {
                    this.stageEl.innerHTML = this.fallbackHtml;
                    return;
                }
                const box = el('div', 'uk-doc-viewer__status uk-text-center');
                box.appendChild(el('p', 'uk-text-muted', text || 'Предпросмотр недоступен'));
                const a = el('button', 'uk-button', '<span uk-icon="icon: cloud-download"></span> Скачать файл');
                a.type = 'button';
                a.addEventListener('click', () => this.download());
                box.appendChild(a);
                this.stageEl.appendChild(box);
            },

            page() { return el('div', 'uk-doc-viewer__page'); },

            async render() {
                const ext = (this.cfg.ext || '').toLowerCase();
                if (!this.cfg.url) { this.setStatus('error'); return; }
                try {
                    if (this.isPdf())   return await this.renderPdf();
                    if (this.isSheet()) return await this.renderSheet();
                    if (this.isWord())  {
                        if (ext === 'doc') return this.setStatus('error', 'Формат .doc не поддерживается для предпросмотра');
                        return await this.renderDocx();
                    }
                    if (this.isText())  return await this.renderText();
                    this.setStatus('error'); // office/unknown → карточка «скачать»
                } catch (e) {
                    this.setStatus('error', 'Не удалось открыть предпросмотр');
                    if (window.console) console.warn('[documentViewer]', e);
                }
            },

            // ---- PDF ------------------------------------------------------------
            async renderPdf() {
                this.setStatus('loading', 'Загрузка PDF…');
                const pdfjsLib = await ensurePdf();
                this._buffer = await fetchBuffer(this.cfg.url);
                const pdf = await pdfjsLib.getDocument({ data: this._buffer.slice(0) }).promise;
                PDFS.set(this, pdf);
                this.pageCount = pdf.numPages;

                this.stageEl.innerHTML = '';
                const first = await pdf.getPage(1);
                const vp = first.getViewport({ scale: 1 });
                this.baseRatio = vp.height / vp.width;

                // Холдеры под все страницы (пустые до рендера)
                this._pages = [];
                for (let i = 1; i <= this.pageCount; i++) {
                    const holder = this.page();
                    holder.dataset.page = i;
                    holder.dataset.rendered = '0';
                    this.stageEl.appendChild(holder);
                    this._pages.push(holder);
                }
                this.updateCounter();
                this.observePages(false); // IO только для счётчика текущей страницы, без ленивого рендера

                // Дожидаемся реальной ширины сцены (панель могла ещё анимироваться / быть скрытой),
                // иначе canvas отрендерился бы в 0px и лист остался бы «пустым».
                await this.whenVisible();
                // Базовая ширина листа при 100% (canvas рендерим по ней; зум делает CSS zoom сцены).
                this._pdfBaseW = this.stageEl.clientWidth || 920;
                // Рендерим страницы последовательно сверху вниз — без «пустых листов».
                // Флаг гасит параллельный refit от ResizeObserver во время первичного прохода.
                this._refitting = true;
                try {
                    for (const holder of this._pages) {
                        try { await this.renderPdfPage(holder); } catch (e) { /* пропускаем сбойную страницу */ }
                    }
                } finally {
                    this._refitting = false;
                }
            },

            // Ждём, пока у сцены появится ненулевая ширина (макс. ~1с), затем резолвим.
            whenVisible(tries = 60) {
                return new Promise((resolve) => {
                    const check = () => {
                        if (!this.stageEl || this.stageEl.clientWidth > 0 || tries-- <= 0) return resolve();
                        requestAnimationFrame(check);
                    };
                    check();
                });
            },

            async renderPdfPage(holder) {
                const pdf = PDFS.get(this);
                if (!pdf) return;
                holder.dataset.rendered = '1';
                const num = parseInt(holder.dataset.page, 10);
                const page = await pdf.getPage(num);
                // Рендерим в «базовую» ширину (без scale) — визуальный зум даёт CSS zoom сцены,
                // как для остальных типов. devicePixelRatio ниже даёт запас чёткости при zoom-in.
                const baseW = this._pdfBaseW || this.stageEl.clientWidth || 920;
                const unit = page.getViewport({ scale: 1 });
                const vp = page.getViewport({ scale: baseW / unit.width });
                const ratio = window.devicePixelRatio || 1;
                const canvas = el('canvas', 'uk-doc-viewer__canvas');
                canvas.width = Math.floor(vp.width * ratio);
                canvas.height = Math.floor(vp.height * ratio);
                canvas.style.width = '100%';
                canvas.style.height = 'auto';
                holder.style.height = '';
                holder.innerHTML = '';
                holder.appendChild(canvas);
                await page.render({
                    canvasContext: canvas.getContext('2d'),
                    viewport: vp,
                    transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null,
                }).promise;
            },

            async refitPdf() {
                if (!this.isPdf() || !this._pages.length) return;
                if (this._refitting) return;      // не запускаем параллельные перерисовки
                this._refitting = true;
                try {
                    for (const h of this._pages) {
                        h.dataset.rendered = '0';
                        try { await this.renderPdfPage(h); } catch (e) { /* пропускаем сбойную страницу */ }
                    }
                } finally {
                    this._refitting = false;
                }
            },

            async pdfPageText(p) {
                if (this._pdfText[p] != null) return this._pdfText[p];
                const pdf = PDFS.get(this);
                const page = await pdf.getPage(p);
                const c = await page.getTextContent();
                return (this._pdfText[p] = c.items.map((i) => i.str).join(' '));
            },

            // ---- DOCX -----------------------------------------------------------
            async renderDocx() {
                this.setStatus('loading', 'Загрузка документа…');
                const docx = await ensureDocx();
                this._buffer = await fetchBuffer(this.cfg.url);
                this.stageEl.innerHTML = '';
                const wrap = el('div', 'uk-doc-viewer__docx');
                this.stageEl.appendChild(wrap);
                // inWrapper:false — без .docx-wrapper: иначе собственное правило docx-preview
                // «.docx-wrapper>section.docx{box-shadow…}» перебивает наш стиль «листа» по
                // специфичности и у Word-страниц нет скругления/тени, как у остальных типов.
                await docx.renderAsync(new Blob([this._buffer]), wrap, null, {
                    className: 'docx', inWrapper: false, breakPages: true,
                    ignoreLastRenderedPageBreak: false, experimental: true, useBase64URL: true,
                });
                this._pages = Array.from(wrap.querySelectorAll('.docx'));
                if (!this._pages.length) this._pages = [wrap];
                this._pages.forEach((p) => p.classList.add('uk-doc-viewer__page'));
                this.pageCount = this._pages.length;
                this.currentPage = 1;
                this.updateCounter();
                this.observePages(false);
            },

            // ---- XLS/XLSX/CSV ---------------------------------------------------
            // xlsx → ExcelJS (со стилями: цвета/шрифты/границы/merge); xls/csv → SheetJS (без стилей).
            async renderSheet() {
                const ext = (this.cfg.ext || '').toLowerCase();
                if (ext === 'xlsx') {
                    try { return await this.renderSheetExcel(); }
                    catch (e) { if (window.console) console.warn('[documentViewer] ExcelJS→SheetJS fallback', e); }
                }
                return await this.renderSheetJs();
            },

            // Перерисовка активного листа нужным движком (для навигации по листам)
            showActiveSheet(n) {
                if (this._excel) this.showExcelSheet(n); else this.showSheet(n);
            },

            // --- SheetJS: xls/csv (и fallback для xlsx), без стилей ---
            async renderSheetJs() {
                this.setStatus('loading', 'Загрузка таблицы…');
                const XLSX = await ensureXlsx();
                this._buffer = await fetchBuffer(this.cfg.url);
                const wb = XLSX.read(this._buffer, { type: 'array' });
                this._xlsx = XLSX;
                this._sheets = wb;
                this._excel = null;
                this.pageCount = wb.SheetNames.length || 1;
                this.currentPage = 1;
                this.stageEl.innerHTML = '';
                this._sheetCard = this.page();
                this.stageEl.appendChild(this._sheetCard);
                this.showSheet(1);
            },

            showSheet(n) {
                if (!this._sheets) return;
                n = Math.min(Math.max(1, n), this.pageCount);
                this.currentPage = n;
                const name = this._sheets.SheetNames[n - 1];
                const ws = this._sheets.Sheets[name];
                const html = ws ? this._xlsx.utils.sheet_to_html(ws, { editable: false }) : '';
                this._sheetCard.innerHTML =
                    '<div class="uk-doc-viewer__sheet-name">' + esc(name || '') + '</div>' +
                    '<div class="uk-doc-viewer__sheet">' + html + '</div>';
                this.updateCounter();
            },

            // --- ExcelJS: xlsx со стилями ---
            async renderSheetExcel() {
                this.setStatus('loading', 'Загрузка таблицы…');
                const ExcelJS = await ensureExcel();
                this._buffer = await fetchBuffer(this.cfg.url);
                const wb = new ExcelJS.Workbook();
                await wb.xlsx.load(this._buffer);
                this._excel = wb;
                this._excelSheets = wb.worksheets || [];
                this._sheets = null;
                this.pageCount = this._excelSheets.length || 1;
                this.currentPage = 1;
                this.stageEl.innerHTML = '';
                this._sheetCard = this.page();
                this.stageEl.appendChild(this._sheetCard);
                this.showExcelSheet(1);
            },

            showExcelSheet(n) {
                if (!this._excelSheets || !this._excelSheets.length) return;
                n = Math.min(Math.max(1, n), this.pageCount);
                this.currentPage = n;
                const ws = this._excelSheets[n - 1];
                this._sheetCard.innerHTML =
                    '<div class="uk-doc-viewer__sheet-name">' + esc(ws.name || '') + '</div>' +
                    '<div class="uk-doc-viewer__sheet">' + this.excelSheetToHtml(ws) + '</div>';
                this.updateCounter();
            },

            // Лист ExcelJS → HTML-таблица с инлайновыми стилями (заливка/шрифт/выравнивание/
            // границы, объединённые ячейки, ширины колонок). Размер ограничиваем, чтобы не раздуть DOM.
            excelSheetToHtml(ws, maxRows = 300, maxCols = 60) {
                const rowsN = Math.min(ws.rowCount || 0, maxRows);
                const colsN = Math.min(ws.columnCount || 0, maxCols);
                if (!rowsN || !colsN) return '<p class="uk-text-meta">Пустой лист</p>';

                // Объединённые ячейки: множество «покрытых» + карта «мастер → span»
                const skip = new Set(), span = {};
                const merges = (ws.model && ws.model.merges) ? ws.model.merges : [];
                merges.forEach((rng) => {
                    const m = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/.exec(String(rng));
                    if (!m) return;
                    const c1 = colToNum(m[1]), r1 = +m[2], c2 = colToNum(m[3]), r2 = +m[4];
                    span[r1 + '_' + c1] = { rs: r2 - r1 + 1, cs: c2 - c1 + 1 };
                    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
                        if (!(r === r1 && c === c1)) skip.add(r + '_' + c);
                    }
                });

                // Ширины колонок (ед. Excel ≈ 7px на символ)
                let colgroup = '<colgroup>';
                for (let c = 1; c <= colsN; c++) {
                    const w = ws.getColumn(c).width;
                    colgroup += '<col' + (w ? ' style="width:' + Math.round(w * 7) + 'px"' : '') + '>';
                }
                colgroup += '</colgroup>';

                let html = '<table class="uk-doc-viewer__xls">' + colgroup;
                for (let r = 1; r <= rowsN; r++) {
                    const row = ws.getRow(r);
                    const hStyle = row.height ? ' style="height:' + row.height + 'pt"' : '';
                    html += '<tr' + hStyle + '>';
                    for (let c = 1; c <= colsN; c++) {
                        const key = r + '_' + c;
                        if (skip.has(key)) continue;
                        const cell = ws.getCell(r, c);
                        const sp = span[key];
                        const attrs = sp ? ((sp.rs > 1 ? ' rowspan="' + sp.rs + '"' : '') + (sp.cs > 1 ? ' colspan="' + sp.cs + '"' : '')) : '';
                        html += '<td' + attrs + this.excelCellStyle(cell) + '>' + esc(cellText(cell)) + '</td>';
                    }
                    html += '</tr>';
                }
                html += '</table>';
                if ((ws.rowCount || 0) > maxRows) {
                    html += '<p class="uk-text-meta">Показаны первые ' + maxRows + ' строк из ' + ws.rowCount + '</p>';
                }
                return html;
            },

            // Инлайновый style="" ячейки из стилей ExcelJS
            excelCellStyle(cell) {
                const s = [];
                const fill = cell.fill;
                if (fill && fill.type === 'pattern' && fill.pattern === 'solid') {
                    const bg = argbToCss(fill.fgColor);
                    if (bg) s.push('background-color:' + bg);
                }
                const f = cell.font;
                if (f) {
                    if (f.bold) s.push('font-weight:600');
                    if (f.italic) s.push('font-style:italic');
                    if (f.underline) s.push('text-decoration:underline');
                    const col = argbToCss(f.color);
                    if (col) s.push('color:' + col);
                    if (f.size) s.push('font-size:' + f.size + 'pt');
                    if (f.name) s.push("font-family:'" + String(f.name).replace(/'/g, '') + "'");
                }
                const a = cell.alignment;
                if (a) {
                    if (a.horizontal) s.push('text-align:' + a.horizontal);
                    if (a.vertical) s.push('vertical-align:' + (a.vertical === 'middle' ? 'middle' : a.vertical));
                    if (a.wrapText) s.push('white-space:normal');
                }
                const b = cell.border;
                if (b) {
                    ['top', 'right', 'bottom', 'left'].forEach((side) => {
                        const bd = b[side];
                        if (bd && bd.style) s.push('border-' + side + ':' + borderCss(bd));
                    });
                }
                return s.length ? ' style="' + s.join(';') + '"' : '';
            },

            // ---- TXT / MD / JSON / код -----------------------------------------
            async renderText() {
                this.setStatus('loading', 'Загрузка…');
                this._buffer = await fetchBuffer(this.cfg.url);
                let text = new TextDecoder('utf-8').decode(this._buffer);
                const ext = (this.cfg.ext || '').toLowerCase();

                const card = this.page();
                card.classList.add('uk-doc-viewer__text');

                if (ext === 'md' || ext === 'markdown') {
                    // убираем YAML front matter, как на сервере
                    text = text.replace(/^---\s*\n[\s\S]*?\n---\s*\n/, '');
                    const marked = await ensureMarked();
                    card.innerHTML = marked ? '<div class="markdown-body">' + marked.parse(text) + '</div>'
                                            : '<pre>' + esc(text) + '</pre>';
                } else if (ext === 'json') {
                    let pretty = text;
                    try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch (e) { /* как есть */ }
                    card.innerHTML = '<pre><code class="language-json">' + esc(pretty) + '</code></pre>';
                } else {
                    const lang = ({ js: 'javascript', py: 'python', sh: 'bash' }[ext]) || ext || 'none';
                    card.innerHTML = '<pre><code class="language-' + esc(lang) + '">' + esc(text) + '</code></pre>';
                }

                this.stageEl.innerHTML = '';
                this.stageEl.appendChild(card);
                if (window.Prism) { try { window.Prism.highlightAllUnder(card); } catch (e) {} }

                this._pages = [card];
                this.pageCount = 1;
                this.currentPage = 1;
                this.updateCounter();
            },

            // ---- Навигация / счётчик -------------------------------------------
            observePages(lazy) {
                if (this.io) this.io.disconnect();
                this.io = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        const h = entry.target;
                        if (lazy && h.dataset.rendered === '0') this.renderPdfPage(h);
                        const idx = this._pages.indexOf(h);
                        if (idx >= 0) { this.currentPage = idx + 1; this.updateCounter(); }
                    });
                }, { root: this.mountEl, rootMargin: '400px 0px', threshold: 0.01 });
                this._pages.forEach((h) => this.io.observe(h));

                if (this.ro) this.ro.disconnect();
                if (this.isPdf()) {
                    let t = null;
                    this.ro = new ResizeObserver(() => {
                        clearTimeout(t);
                        t = setTimeout(() => {
                            // Пересчитываем базу и перерисовываем только на 100% (при зуме размер
                            // держит CSS zoom, повторный ре-рендер не нужен и сбил бы базу).
                            if (this.scale === 1) {
                                this._pdfBaseW = this.stageEl.clientWidth || this._pdfBaseW;
                                this.refitPdf();
                            }
                        }, 150);
                    });
                    this.ro.observe(this.mountEl);
                }
            },

            go(dir) {
                if (this.isSheet()) { this.showActiveSheet(this.currentPage + dir); return; }
                const i = Math.min(Math.max(0, this.currentPage - 1 + dir), this._pages.length - 1);
                if (this._pages[i]) this._pages[i].scrollIntoView({ behavior: 'smooth', block: 'start' });
            },

            updateCounter() {
                if (this.els.counter) this.els.counter.textContent = this.currentPage + ' / ' + this.pageCount;
                const single = this.pageCount <= 1;
                if (this.els.prev) this.els.prev.disabled = single;
                if (this.els.next) this.els.next.disabled = single;
            },

            // ---- Зум ------------------------------------------------------------
            // Единый механизм для всех типов: CSS zoom сцены (layout-масштаб, mount корректно
            // скроллится). PDF-canvas уже отрендерен с запасом по devicePixelRatio.
            zoom(delta) {
                this.scale = Math.min(3, Math.max(0.4, +(this.scale + delta).toFixed(2)));
                if (this.els.zoom) this.els.zoom.textContent = Math.round(this.scale * 100) + '%';
                this.stageEl.style.zoom = this.scale;
            },

            // ---- Поиск ----------------------------------------------------------
            toggleSearch(force) {
                const show = force != null ? force : !this.root.classList.contains('uk-doc-viewer--searching');
                this.root.classList.toggle('uk-doc-viewer--searching', show);
                if (show) { this.els.search.focus(); }
                else { this.els.search.value = ''; this.runSearch(''); }
            },

            clearMarks() {
                const scope = this.scope();
                if (!scope) return;
                scope.querySelectorAll('mark.dv-hit').forEach((m) => {
                    const t = document.createTextNode(m.textContent);
                    m.replaceWith(t);
                });
                scope.normalize();
            },

            async runSearch(q) {
                this._hits = []; this._hitIdx = -1; this._pdfMatches = [];
                this.clearMarks();
                q = (q || '').trim();
                if (!q) { this.els.sCount.textContent = ''; return; }

                if (this.isPdf()) {
                    const ql = q.toLowerCase();
                    for (let p = 1; p <= this.pageCount; p++) {
                        const t = await this.pdfPageText(p);
                        if (t.toLowerCase().includes(ql)) this._pdfMatches.push(p);
                    }
                    if (this._pdfMatches.length) { this._hitIdx = 0; this.gotoPdfMatch(0); }
                    this.els.sCount.textContent = this._pdfMatches.length ? ('стр. ' + this._pdfMatches.length) : 'нет';
                    return;
                }

                const scope = this.scope();
                const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null);
                const nodes = [];
                while (walker.nextNode()) {
                    const n = walker.currentNode;
                    const pn = n.parentNode ? n.parentNode.nodeName : '';
                    if (pn === 'SCRIPT' || pn === 'STYLE' || pn === 'MARK') continue;
                    if (n.nodeValue && n.nodeValue.toLowerCase().includes(q.toLowerCase())) nodes.push(n);
                }
                nodes.forEach((n) => this.wrapMatches(n, q));
                this._hits = Array.from(scope.querySelectorAll('mark.dv-hit'));
                this.els.sCount.textContent = this._hits.length ? ('1 / ' + this._hits.length) : 'нет';
                if (this._hits.length) { this._hitIdx = 0; this.markActive(); }
            },

            wrapMatches(node, q) {
                const text = node.nodeValue, low = text.toLowerCase(), ql = q.toLowerCase();
                let idx, from = 0, found = false;
                const frag = document.createDocumentFragment();
                while ((idx = low.indexOf(ql, from)) !== -1) {
                    found = true;
                    if (idx > from) frag.appendChild(document.createTextNode(text.slice(from, idx)));
                    const m = el('mark', 'dv-hit'); m.textContent = text.slice(idx, idx + ql.length);
                    frag.appendChild(m);
                    from = idx + ql.length;
                }
                if (found) {
                    if (from < text.length) frag.appendChild(document.createTextNode(text.slice(from)));
                    node.parentNode.replaceChild(frag, node);
                }
            },

            hit(dir) {
                if (this.isPdf()) {
                    if (!this._pdfMatches.length) return;
                    this._hitIdx = (this._hitIdx + dir + this._pdfMatches.length) % this._pdfMatches.length;
                    this.gotoPdfMatch(this._hitIdx);
                    return;
                }
                if (!this._hits.length) return;
                this._hitIdx = (this._hitIdx + dir + this._hits.length) % this._hits.length;
                this.markActive();
            },

            markActive() {
                this._hits.forEach((m) => m.classList.remove('dv-hit-active'));
                const m = this._hits[this._hitIdx];
                if (m) { m.classList.add('dv-hit-active'); m.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
                this.els.sCount.textContent = (this._hitIdx + 1) + ' / ' + this._hits.length;
            },

            gotoPdfMatch(i) {
                const p = this._pdfMatches[i];
                if (!p) return;
                const holder = this._pages[p - 1];
                if (holder) holder.scrollIntoView({ behavior: 'smooth', block: 'start' });
                this.els.sCount.textContent = (i + 1) + ' / ' + this._pdfMatches.length;
            },

            // ---- Копировать / ссылка / скачать ----------------------------------
            async copyAll() {
                let text = '';
                try {
                    if (this.isPdf()) {
                        for (let p = 1; p <= this.pageCount; p++) text += (await this.pdfPageText(p)) + '\n\n';
                    } else {
                        text = this.scope().innerText || '';
                    }
                    await navigator.clipboard.writeText(text.trim());
                    notify('Текст скопирован', 'success');
                } catch (e) { notify('Не удалось скопировать', 'warning'); }
            },

            copyLink() {
                let u = this.cfg.url;
                if (this.isPdf()) u += '#page=' + this.currentPage;
                navigator.clipboard.writeText(u).then(
                    () => notify('Ссылка скопирована', 'success'),
                    () => notify('Не удалось скопировать ссылку', 'warning')
                );
            },

            async download() {
                try {
                    const buf = this._buffer || await fetchBuffer(this.cfg.url);
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(new Blob([buf]));
                    a.download = this.cfg.name || 'document';
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                } catch (e) {
                    if (this.cfg.url) window.open(this.cfg.url, '_blank');
                }
            },
        };
    };

    // Императивный реестр: одна оболочка на элемент .uk-doc-viewer-wrap.
    // (Пере)инициализацию дёргает событие offcanvas-file (см. chat.blade.php), а не
    // Alpine/Livewire-morph — это убирает проблемы с ре-инициализацией при смене файла
    // (morph то переиспользует компонент, то нет).
    const CONTROLLERS = new WeakMap();
    window.documentViewerOpen = function (cfg) {
        document.querySelectorAll('.uk-doc-viewer-wrap').forEach((root) => {
            if (!root.isConnected) return;
            // пропускаем скрытый дубль (моб/пк), если оба вдруг оказались в DOM
            if (root.offsetParent === null && root.getClientRects().length === 0) return;
            let ctrl = CONTROLLERS.get(root);
            if (!ctrl) {
                ctrl = window.documentViewer(cfg);
                ctrl.root = root;
                CONTROLLERS.set(root, ctrl);
                ctrl.boot();
            }
            ctrl.open(cfg);
        });
    };
})();
